import { useEffect } from 'react';
import { api } from '@/lib/api';

export type TechnicalReportKind = 'OfflineSyncFailure' | 'ApplicationError';

export interface TechnicalReport {
  kind: TechnicalReportKind; code: string; detail: string; count: number;
  firstOccurredAt: string; lastOccurredAt: string;
}

const MAX_REPORTS = 50;
const storageKey = (actorId: string, eventId: string) => `meridian:event-technical-reports:${actorId}:${eventId}`;
const inFlight = new Map<string, Promise<void>>();

// Reports keep only what triage needs: no attendee fields, and addresses or long numbers are masked.
export function redactTechnicalDetail(value: string): string {
  const text = value.replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[email]').replace(/\d{7,}/g, '[number]').trim();
  return text.length > 300 ? text.slice(0, 300) : text;
}

function read(key: string): TechnicalReport[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed as TechnicalReport[] : [];
  } catch { return []; }
}

function write(key: string, reports: TechnicalReport[]) {
  try {
    if (reports.length) localStorage.setItem(key, JSON.stringify(reports.slice(-MAX_REPORTS)));
    else localStorage.removeItem(key);
  } catch { /* storage unavailable: reports stay best-effort */ }
}

function merge(existing: TechnicalReport[], incoming: TechnicalReport[]): TechnicalReport[] {
  const merged = [...existing];
  for (const report of incoming) {
    const same = merged.find((item) => item.kind === report.kind && item.code === report.code && item.detail === report.detail);
    if (!same) { merged.push({ ...report }); continue; }
    same.count += report.count;
    if (report.firstOccurredAt < same.firstOccurredAt) same.firstOccurredAt = report.firstOccurredAt;
    if (report.lastOccurredAt > same.lastOccurredAt) same.lastOccurredAt = report.lastOccurredAt;
  }
  return merged;
}

export function queueTechnicalReport(actorId: string, eventId: string, kind: TechnicalReportKind, code: string, detail: string, at = new Date()) {
  if (!actorId || !eventId) return;
  const key = storageKey(actorId, eventId);
  const time = at.toISOString();
  write(key, merge(read(key), [{ kind, code: code.slice(0, 100) || 'Unknown', detail: redactTechnicalDetail(detail), count: 1, firstOccurredAt: time, lastOccurredAt: time }]));
}

/** Sends queued reports in one batch; on failure they are merged back for the next attempt. */
export function flushTechnicalReports(actorId: string, eventId: string): Promise<void> {
  const key = storageKey(actorId, eventId);
  const running = inFlight.get(key);
  if (running) return running;
  const run = (async () => {
    const reports = read(key);
    if (!reports.length || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
    write(key, []);
    try {
      await api.post(`/events/${eventId}/technical-reports`, { commandId: crypto.randomUUID(), reports }, { timeout: 10_000, headers: { 'X-Meridian-Event-Actor': actorId } });
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      // A rejected batch or a lost event membership will not succeed later; everything else retries.
      if (status !== 400 && status !== 403 && status !== 404) write(key, merge(read(key), reports));
    }
  })();
  inFlight.set(key, run);
  return run.finally(() => inFlight.delete(key));
}

export function reportTechnicalIssue(actorId: string, eventId: string, kind: TechnicalReportKind, code: string, detail: string) {
  queueTechnicalReport(actorId, eventId, kind, code, detail);
  void flushTechnicalReports(actorId, eventId);
}

const errorCode = (error: unknown) => error instanceof Error ? error.name || 'Error' : typeof error;
const errorDetail = (error: unknown) => error instanceof Error ? error.message : typeof error === 'string' ? error : '';

// API failures already surface server-side (and as sync failures), so only client faults are reported here.
const isApiError = (error: unknown) => !!(error as { isAxiosError?: boolean } | null)?.isAxiosError;

// The route error boundary replaces the workspace before it can report, so the last open event is remembered.
let lastScope: { actorId: string; eventId: string } | null = null;

export function reportEventRouteError(pathname: string, error: unknown) {
  if (!lastScope || !(error instanceof Error) || !pathname.startsWith('/events/') || !pathname.includes(lastScope.eventId)) return;
  reportTechnicalIssue(lastScope.actorId, lastScope.eventId, 'ApplicationError', errorCode(error), errorDetail(error));
}

/** While an event workspace is open: capture uncaught errors and send queued reports when online. */
export function useEventTechnicalReporting(actorId: string | undefined, eventId: string) {
  useEffect(() => {
    if (!actorId || !eventId) return;
    lastScope = { actorId, eventId };
    const flush = () => { void flushTechnicalReports(actorId, eventId); };
    const onError = (event: ErrorEvent) => reportTechnicalIssue(actorId, eventId, 'ApplicationError', errorCode(event.error), errorDetail(event.error) || event.message);
    const onRejection = (event: PromiseRejectionEvent) => {
      if (!isApiError(event.reason)) reportTechnicalIssue(actorId, eventId, 'ApplicationError', errorCode(event.reason), errorDetail(event.reason));
    };
    window.addEventListener('online', flush);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    flush();
    return () => {
      window.removeEventListener('online', flush);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [actorId, eventId]);
}
